// src/components/OrderHistoryCard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { paymentStatusLabel, isOrderPaidStatus, formatOrderDate } from '../utils/orderUtils';

/**
 * Displays a single past order in the purchase history tab.
 * Shows order metadata, product list, total, status badge,
 * and a download button for paid orders.
 */
export default function OrderHistoryCard({ order, onDownload, isDownloading }) {
  const { label, color } = paymentStatusLabel(order.paymentStatus);
  const isPaid = isOrderPaidStatus(order);
  const date = formatOrderDate(order.creadoEn);

  return (
    <div className="card mb-3 shadow-sm order-history-card">
      <div className="card-body">
        {/* Header: order # + status badge */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0 fw-bold">Orden #{order.id}</h6>
          <span className={`badge bg-${color}`}>{label}</span>
        </div>

        {/* Date */}
        <p className="text-muted small mb-2">{date}</p>

        {/* Product list */}
        {Array.isArray(order.items) && order.items.length > 0 && (
          <ul className="list-unstyled mb-2">
            {order.items.map((item, idx) => (
              <li key={idx} className="text-muted small">
                • {item.nombre ?? item.name ?? `Producto #${item.productId}`}
                {item.quantity > 1 && ` ×${item.quantity}`}
                {item.precioUnitario != null && ` — $${Number(item.precioUnitario).toFixed(2)}`}
              </li>
            ))}
          </ul>
        )}

        {/* Footer: total + download button */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="fw-bold">
            Total: {order.total != null ? `$${Number(order.total).toFixed(2)}` : '—'}
          </span>
          {isPaid && (
            <button
              className="btn btn-sm btn-outline-success"
              disabled={isDownloading}
              onClick={() => onDownload(order)}
            >
              {isDownloading ? 'Descargando…' : '⬇ Descargar ZIP'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

OrderHistoryCard.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.number.isRequired,
    paymentStatus: PropTypes.string,
    creadoEn: PropTypes.string,
    total: PropTypes.number,
    downloadUrl: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      nombre: PropTypes.string,
      name: PropTypes.string,
      productId: PropTypes.number,
      quantity: PropTypes.number,
      precioUnitario: PropTypes.number,
    })),
  }).isRequired,
  onDownload: PropTypes.func.isRequired,
  isDownloading: PropTypes.bool,
};

OrderHistoryCard.defaultProps = {
  isDownloading: false,
};
